import { MutationTree } from 'vuex';
import { State } from './state';
import {
  SetOpportunityFieldValuePayload,
  OpportunityResponse,
  OpportunityFieldResponse,
  SaveOpportunityResponse,
  SetOpportunitiesByTaskPayload,
} from './types';
import { find } from 'lodash';
import { Task } from '@/types/Task';
import { TaskStatus } from '@/enums/Tasks';

export enum OpportunitiesMutationTypes {
  SET_ACTION_TASKS = 'SET_ACTION_TASKS',
  SET_ACTION_TASKS_LOADING = 'SET_ACTION_TASKS_LOADING',
  SET_TASK_IN_PROGRESS = 'SET_TASK_IN_PROGRESS',
  SET_OPPORTUNITIES = 'SET_OPPORTUNITIES',
  SET_OPPORTUNITY_FIELD_VALUE = 'SET_OPPORTUNITY_FIELD_VALUE',
  SET_OPPORTUNITY_FIELDS_AFTER_CALCULATION = 'SET_OPPORTUNITY_FIELDS_AFTER_CALCULATION',
  SET_VALIDATION = 'SET_VALIDATION',
  SET_IS_LOADING = 'SET_IS_LOADING',
  SET_POTENTIAL_OPPORTUNITIES = 'GET_POTENTIAL_OPPORTUNITIES',
  SET_IS_MODAL_LOADING = 'SET_IS_MODAL_LOADING',
  SET_IS_MODAL_SUBMITTING = 'SET_IS_MODAL_SUBMITTING',
  RESET_OPPORTUNITIES_STATE = 'RESET_OPPORTUNITIES_STATE',
  SET_LAST_MADE_TASK = 'SET_LAST_MADE_TASK',
}

export type Mutations<S = State> = {
  [OpportunitiesMutationTypes.SET_ACTION_TASKS](
    state: S,
    payload: Task[]
  ): void;
  [OpportunitiesMutationTypes.SET_ACTION_TASKS_LOADING](
    state: S,
    payload: boolean
  ): void;
  [OpportunitiesMutationTypes.SET_TASK_IN_PROGRESS](
    state: S,
    payload: string
  ): void;
  [OpportunitiesMutationTypes.SET_OPPORTUNITIES](
    state: S,
    payload: SetOpportunitiesByTaskPayload
  ): void;
  [OpportunitiesMutationTypes.SET_OPPORTUNITY_FIELD_VALUE](
    state: S,
    payload: SetOpportunityFieldValuePayload
  ): void;
  [OpportunitiesMutationTypes.SET_OPPORTUNITY_FIELDS_AFTER_CALCULATION](
    state: S,
    payload: {
      taskId: string;
      opportunity: OpportunityResponse;
    }
  ): void;
  [OpportunitiesMutationTypes.SET_VALIDATION](
    state: S,
    payload: {
      taskId: string;
      opportunityRes: SaveOpportunityResponse;
    }
  ): void;
  [OpportunitiesMutationTypes.SET_IS_LOADING](state: S, payload: boolean): void;
  [OpportunitiesMutationTypes.SET_IS_MODAL_LOADING](
    state: S,
    payload: boolean
  ): void;
  [OpportunitiesMutationTypes.SET_IS_MODAL_SUBMITTING](
    state: S,
    payload: boolean
  ): void;
  [OpportunitiesMutationTypes.SET_POTENTIAL_OPPORTUNITIES](
    state: S,
    payload: Array<OpportunityResponse>
  ): void;
  [OpportunitiesMutationTypes.RESET_OPPORTUNITIES_STATE](state: S): void;
  [OpportunitiesMutationTypes.SET_LAST_MADE_TASK](
    state: S,
    payload: string
  ): void;
};

const findFieldFromOpportunity = (
  fields: Array<OpportunityFieldResponse>,
  fieldName: string
) => {
  return find(fields, x => x.name.dataValue === fieldName);
};

export const mutations: MutationTree<State> & Mutations = {
  [OpportunitiesMutationTypes.SET_ACTION_TASKS](state, payload) {
    state.tasks = payload;
  },
  [OpportunitiesMutationTypes.SET_ACTION_TASKS_LOADING](state, payload) {
    state.tasksLoading = payload;
  },
  [OpportunitiesMutationTypes.SET_TASK_IN_PROGRESS](state, payload) {
    const task = state.tasks.find(task => task.taskId === payload);

    if (task && task.taskStatus !== TaskStatus.IN_PROGRESS) {
      task.taskStatus = TaskStatus.IN_PROGRESS;
    }
  },
  [OpportunitiesMutationTypes.SET_OPPORTUNITIES](state, payload) {
    state.opportunities[payload.taskId] = payload.opportunities;
  },
  [OpportunitiesMutationTypes.SET_OPPORTUNITY_FIELD_VALUE](state, payload) {
    const opportunity = state.opportunities[payload.taskId];
    if (opportunity && opportunity.fields && opportunity.fields.length > 0) {
      const field = findFieldFromOpportunity(
        opportunity.fields,
        payload.fieldName
      );
      if (field) {
        field.value.dataValue = payload.value;
      }
    }
  },
  [OpportunitiesMutationTypes.SET_OPPORTUNITY_FIELDS_AFTER_CALCULATION](
    state,
    payload
  ) {
    const opportunity = state.opportunities[payload.taskId];
    if (opportunity) {
      opportunity.fields = opportunity.fields.map(field => {
        const updatedField = payload.opportunity.fields.find(
          newField => field.name.dataValue === newField.name.dataValue
        );
        return updatedField ? updatedField : field;
      });
    }
  },
  [OpportunitiesMutationTypes.SET_VALIDATION](state, payload) {
    const opportunity = state.opportunities[payload.taskId];
    if (opportunity) {
      opportunity.passesValidation = payload.opportunityRes.passesValidation;
    }
  },
  [OpportunitiesMutationTypes.SET_IS_LOADING](state, payload) {
    state.isLoading = payload;
  },
  [OpportunitiesMutationTypes.SET_IS_MODAL_LOADING](state, payload) {
    state.isModalLoading = payload;
  },
  [OpportunitiesMutationTypes.SET_IS_MODAL_SUBMITTING](state, payload) {
    state.isModalSubmitting = payload;
  },
  [OpportunitiesMutationTypes.SET_POTENTIAL_OPPORTUNITIES](state, payload) {
    state.potentialOps = payload;
  },
  [OpportunitiesMutationTypes.RESET_OPPORTUNITIES_STATE](state) {
    state.tasks = [];
    state.tasksLoading = false;
    state.isLoading = false;
    state.isModalLoading = false;
    state.isModalSubmitting = false;
    state.opportunities = {};
    state.potentialOps = [];
    state.lastMadeTaskId = null;
  },
  [OpportunitiesMutationTypes.SET_LAST_MADE_TASK](state, payload) {
    state.lastMadeTaskId = payload;
  },
};
